import { setDeleteFileModal, setSelectedFileType } from '../../../../../store/workspace/slice';
import { DownloadIcon, EditIcon, PDFIcon, TrashIcon } from '../../../../../assets/icons/common';
import { downloadFileThunk, uploadFileThunk } from '../../../../../store/general/thunks';
import { updateProfileDataThunk } from '../../../../../store/workspace/thunks';
import { selectProfileData } from '../../../../../store/workspace/selectors';
import { AccountFileType } from '../../../../../store/workspace/types';
import { useDispatch, useSelector } from '../../../../../store/store';
import { setNotification } from '../../../../../store/general/slice';
import { selectUserId } from '../../../../../store/auth/selectors';
import DropBox from '../../../../../containers/DropBox';
import styles from './style.module.scss';
import { useState } from 'react';

interface Props {
    resumeFileKey: string | undefined;
    certificateFileKey: string | undefined;
    coverLetterFileKey: string | undefined;
    portfolioFileKey: string | undefined;
};

export default function UploadedFiles(props: Props) {
    const dispatch = useDispatch();
    const userId = useSelector(selectUserId);
    const profile = useSelector(selectProfileData);
    const [uploading, setUploading] = useState(false);
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [portfolioFile, setPortfolioFile] = useState<File | null>(null);
    const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
    const [certificateFile, setCertificateFile] = useState<File | null>(null);
    const [editMode, setEditMode] = useState<AccountFileType>('None');

    const {
        resumeFileKey,
        portfolioFileKey,
        certificateFileKey,
        coverLetterFileKey,
    } = props;

    const uploadResumeFile = async () => {
        if (!userId || !profile || !resumeFile) return;
        const { kyc, matchedJobs, setting, ...rest } = profile;

        try {
            setUploading(true);
            await dispatch(uploadFileThunk({ fileName: `${userId}/${resumeFile.name}`, file: resumeFile }));
            await dispatch(updateProfileDataThunk({ ...rest, resumeFileKey: resumeFile.name }));
            setUploading(false);
            setEditMode('None');
        } catch (e: unknown) {
            setUploading(false);
            dispatch(setNotification(['Somthing went wrong. Please try again', 'error']))
        }
    };

    const uploadPortfolioFile = async () => {
        if (!userId || !profile || !portfolioFile) return;
        const { kyc, matchedJobs, setting, ...rest } = profile;

        try {
            setUploading(true);
            await dispatch(uploadFileThunk({ fileName: `${userId}/${portfolioFile.name}`, file: portfolioFile }));
            dispatch(updateProfileDataThunk({ ...rest, portfolioFileKey: portfolioFile.name }));
            setUploading(false);
            setEditMode('None');
        } catch (e: unknown) {
            setUploading(false);
            dispatch(setNotification(['Somthing went wrong. Please try again', 'error']))
        }
    };

    const uploadCoverLetterFile = async () => {
        if (!userId || !profile || !coverLetterFile) return;
        const { kyc, matchedJobs, setting, ...rest } = profile;

        try {
            setUploading(true);
            await dispatch(uploadFileThunk({ fileName: `${userId}/${coverLetterFile.name}`, file: coverLetterFile }));
            dispatch(updateProfileDataThunk({ ...rest, coverLetterFileKey: coverLetterFile.name }));
            setUploading(false);
            setEditMode('None');
        } catch (e: unknown) {
            setUploading(false);
            dispatch(setNotification(['Somthing went wrong. Please try again', 'error']))
        }
    };

    const uploadCertificateFile = async () => {
        if (!userId || !profile || !certificateFile) return;
        const { kyc, matchedJobs, setting, ...rest } = profile;

        try {
            setUploading(true);
            await dispatch(uploadFileThunk({ fileName: `${userId}/${certificateFile.name}`, file: certificateFile }));
            dispatch(updateProfileDataThunk({ ...rest, certificateFileKey: certificateFile.name }));
            setUploading(false);
            setEditMode('None');
        } catch (e: unknown) {
            setUploading(false);
            dispatch(setNotification(['Somthing went wrong. Please try again', 'error']))
        }
    };

    const downloadFile = (path: string, fileName: string) => {
        dispatch(downloadFileThunk({ path, fileName }));
    };

    const deleteFile = (fileType: AccountFileType) => {
        dispatch(setSelectedFileType(fileType));
        dispatch(setDeleteFileModal(true));
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <div className={styles.content_headbar}>
                    <span className={styles.content_headbar_title}>Resume</span>
                    <div className={styles.content_headbar_actions}>
                        <EditIcon onClick={() => setEditMode('Resume')} />
                        {resumeFileKey && <>
                            <TrashIcon onClick={() => deleteFile('Resume')} />
                            <DownloadIcon onClick={() => downloadFile(`${userId}/${resumeFileKey}`, resumeFileKey)} />
                        </>}
                    </div>
                </div>
                <div className={styles.content_main}>
                    {resumeFileKey && <div className={styles.content_main_row}>
                        <PDFIcon />
                        <span className={styles.content_main_row_title}>{resumeFileKey}</span>
                    </div>}
                    {!resumeFileKey && editMode !== 'Resume' && <span className={styles.content_main_noData}>No file.</span>}
                    {editMode === 'Resume' && <DropBox
                        fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                        file={resumeFile}
                        uploading={uploading}
                        setFile={setResumeFile}
                        uploadFunc={uploadResumeFile}
                        description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                    />}
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.content_headbar}>
                    <span className={styles.content_headbar_title}>Portfolio</span>
                    <div className={styles.content_headbar_actions}>
                        <EditIcon onClick={() => setEditMode('Portfolio')} />
                        {portfolioFileKey && <>
                            <TrashIcon onClick={() => deleteFile('Portfolio')} />
                            <DownloadIcon onClick={() => downloadFile(`${userId}/${portfolioFileKey}`, portfolioFileKey)} />
                        </>}
                    </div>
                </div>
                <div className={styles.content_main}>
                    {portfolioFileKey && <div className={styles.content_main_row}>
                        <PDFIcon />
                        <span className={styles.content_main_row_title}>{portfolioFileKey}</span>
                    </div>}
                    {!portfolioFileKey && editMode !== 'Portfolio' && <span className={styles.content_main_noData}>No file.</span>}
                    {editMode === 'Portfolio' && <DropBox
                        fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                        file={portfolioFile}
                        setFile={setPortfolioFile}
                        uploading={uploading}
                        uploadFunc={uploadPortfolioFile}
                        description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                    />}
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.content_headbar}>
                    <span className={styles.content_headbar_title}>Cover letter</span>
                    <div className={styles.content_headbar_actions}>
                        <EditIcon onClick={() => setEditMode('Cover')} />
                        {coverLetterFileKey && <>
                            <TrashIcon onClick={() => deleteFile('Cover')} />
                            <DownloadIcon onClick={() => downloadFile(`${userId}/${coverLetterFileKey}`, coverLetterFileKey)} />
                        </>}
                    </div>
                </div>
                <div className={styles.content_main}>
                    {coverLetterFileKey && <div className={styles.content_main_row}>
                        <PDFIcon />
                        <span className={styles.content_main_row_title}>{coverLetterFileKey}</span>
                    </div>}
                    {!coverLetterFileKey && editMode !== 'Cover' && <span className={styles.content_main_noData}>No file.</span>}
                    {editMode === 'Cover' && <DropBox
                        fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                        file={coverLetterFile}
                        setFile={setCoverLetterFile}
                        uploading={uploading}
                        uploadFunc={uploadCoverLetterFile}
                        description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                    />}
                </div>
            </div>

            <div className={styles.content}>
                <div className={styles.content_headbar}>
                    <span className={styles.content_headbar_title}>Certificate</span>
                    <div className={styles.content_headbar_actions}>
                        <EditIcon onClick={() => setEditMode('Certificate')} />
                        {certificateFileKey && <>
                            <TrashIcon onClick={() => deleteFile('Certificate')} />
                            <DownloadIcon onClick={() => downloadFile(`${userId}/${certificateFileKey}`, certificateFileKey)} />
                        </>}
                    </div>
                </div>
                <div className={styles.content_main}>
                    {certificateFileKey && <div className={styles.content_main_row}>
                        <PDFIcon />
                        <span className={styles.content_main_row_title}>{certificateFileKey}</span>
                    </div>}
                    {!certificateFileKey && editMode !== 'Certificate' && <span className={styles.content_main_noData}>No file.</span>}
                    {editMode === 'Certificate' && <DropBox
                        fileTypes={{ 'application/pdf': ['.pdf'], 'application/doc': ['.doc', '.docx'] }}
                        file={certificateFile}
                        setFile={setCertificateFile}
                        uploading={uploading}
                        uploadFunc={uploadCertificateFile}
                        description='PDF, Doc or Docx , Maximum upload file size: 12MB'
                    />}
                </div>
            </div>
        </div>
    )
}
